import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, CheckCircle, AlertTriangle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { SEO } from '../components/SEO';

const timeline = [
  {
    period: "Freshman Year (9th Grade)",
    priority: "low",
    theme: "Build Foundations",
    items: [
      { text: "Focus on academics — GPA matters from Day 1", critical: false },
      { text: "Explore 5-7 extracurriculars to discover genuine interests", critical: false },
      { text: "Start building relationships with teachers", critical: false },
      { text: "Begin thinking about summer plans (camps, volunteering, jobs)", critical: false },
    ],
    tip: "Don't stress about 'the college list' yet. Focus on discovering what you actually care about.",
  },
  {
    period: "Sophomore Year (10th Grade)",
    priority: "medium",
    theme: "Deepen & Specialize",
    items: [
      { text: "Narrow extracurriculars to 3-4 where you can lead or create", critical: false },
      { text: "Take the PSAT as practice", critical: false },
      { text: "Start a passion project if you have a clear interest", critical: true },
      { text: "Research summer programs (RSI, TASP, Governor's School deadlines are fall of junior year)", critical: true },
      { text: "Consider AP/honors course load for junior year", critical: false },
    ],
    tip: "This is when your 'narrative' starts forming. Admissions officers will look for a thread connecting your activities, courses, and interests.",
  },
  {
    period: "Junior Year — Fall",
    priority: "high",
    theme: "Strategy Begins",
    items: [
      { text: "Take the SAT or ACT (first attempt)", critical: true },
      { text: "Research colleges seriously — start building your school list", critical: true },
      { text: "Visit campuses if possible (virtual tours count)", critical: false },
      { text: "Apply to summer programs (many deadlines are Nov-Feb)", critical: true },
      { text: "Meet with your school counselor", critical: false },
      { text: "Begin identifying potential recommenders", critical: false },
    ],
    tip: "Junior fall is when most premium consulting clients begin. If you're starting here, you're right on time.",
  },
  {
    period: "Junior Year — Spring",
    priority: "high",
    theme: "Test & Position",
    items: [
      { text: "Take SAT/ACT again if needed", critical: true },
      { text: "Finalize school list (reaches, targets, safeties)", critical: true },
      { text: "Ask recommenders formally — give them time", critical: true },
      { text: "Start brainstorming Common App personal statement", critical: true },
      { text: "Take AP exams", critical: false },
      { text: "Attend admitted student events at schools you're considering", critical: false },
    ],
    tip: "By the end of junior year, you should know your school list and have recommendation writers confirmed. The summer is for essays.",
  },
  {
    period: "Summer Before Senior Year",
    priority: "critical",
    theme: "Essay Season",
    items: [
      { text: "Write the Common App personal statement — aim for 2-3 complete drafts", critical: true },
      { text: "Begin supplemental essays for early-round schools", critical: true },
      { text: "Finalize your activities list in Common App format", critical: true },
      { text: "Research each school's supplemental prompts deeply", critical: true },
      { text: "Organize all documents: transcripts, test scores, awards", critical: false },
      { text: "Practice interview questions", critical: false },
    ],
    tip: "This is the single most important period. Students who enter senior year with essays drafted have a massive advantage. Don't waste this summer.",
  },
  {
    period: "Senior Year — September/October",
    priority: "critical",
    theme: "Early Round Preparation",
    items: [
      { text: "Finalize all Early Decision/Early Action essays", critical: true },
      { text: "Submit ED/EA applications by November 1 or November 15", critical: true },
      { text: "Send test scores to schools", critical: true },
      { text: "Confirm recommendation letters are submitted", critical: true },
      { text: "Complete FAFSA and CSS Profile (opens October 1)", critical: true },
      { text: "Continue working on Regular Decision supplementals", critical: false },
    ],
    tip: "Early Decision acceptance rates are 3-4x higher than Regular Decision at most top schools. If you have a clear first choice, use it.",
  },
  {
    period: "Senior Year — November/December",
    priority: "critical",
    theme: "Regular Decision Push",
    items: [
      { text: "Receive Early Decision/Early Action results (mid-December)", critical: false },
      { text: "Finalize all Regular Decision essays", critical: true },
      { text: "Submit RD applications (most deadlines: Jan 1-15)", critical: true },
      { text: "Apply for scholarships with December/January deadlines", critical: true },
      { text: "Prepare for alumni interviews (if offered)", critical: false },
    ],
    tip: "If you're deferred from ED, write a strong Letter of Continued Interest. If you're accepted, celebrate — you're done.",
  },
  {
    period: "Senior Year — January-March",
    priority: "medium",
    theme: "Wait & Prepare",
    items: [
      { text: "Complete any remaining RD applications (Jan deadlines)", critical: true },
      { text: "Complete alumni interviews as they're scheduled", critical: false },
      { text: "Submit mid-year school report (your counselor handles this)", critical: false },
      { text: "Continue applying for scholarships", critical: false },
      { text: "Regular Decision results arrive (late March/early April)", critical: false },
    ],
    tip: "This is the hardest part — waiting. Focus on keeping your grades up. Colleges will see your mid-year and final transcripts.",
  },
  {
    period: "April — Decision Time",
    priority: "high",
    theme: "Choose Wisely",
    items: [
      { text: "Compare financial aid packages carefully", critical: true },
      { text: "Attend admitted student events (virtual or in-person)", critical: false },
      { text: "Submit enrollment deposit by May 1 (National Decision Day)", critical: true },
      { text: "Notify schools you're declining", critical: false },
      { text: "Consider waitlist strategy if needed", critical: false },
    ],
    tip: "Don't just pick the 'highest ranked' school. Pick the best fit for you — academically, financially, and culturally.",
  },
];

const priorityColors = { low: "bg-blue-50 text-blue-700 border-blue-200", medium: "bg-yellow-50 text-yellow-700 border-yellow-200", high: "bg-orange-50 text-orange-700 border-orange-200", critical: "bg-red-50 text-red-700 border-red-200" };

export default function TimelineGuidePage() {
  return (
    <div className="min-h-screen bg-white" data-testid="timeline-guide-page">
      <SEO title="Application Timeline — Month-by-Month Guide from 9th Grade to Decision Day" description="The complete college application timeline from freshman year to May 1 decision day. When to take the SAT, when to start essays, early decision deadlines, and every milestone in between." />
      <MarketingNav />

      <section className="pt-16 bg-[#0F392B] py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4">From 9th Grade to Decision Day</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            The Complete Application <span className="italic">Timeline</span>
          </h1>
          <p className="text-base text-white/70 max-w-xl mx-auto">
            Every milestone, every deadline, every critical task — mapped out so you never miss a beat.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative">
            <div className="absolute left-4 md:left-8 top-0 bottom-0 w-px bg-[#E2E2E2]" />
            <div className="space-y-12">
              {timeline.map((phase, i) => (
                <div key={i} className="relative pl-12 md:pl-20" data-testid={`timeline-phase-${i}`}>
                  <div className="absolute left-2 md:left-6 top-1 w-4 h-4 rounded-full bg-[#0F392B] border-4 border-white shadow-md" />
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                    <h3 className="text-lg font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>{phase.period}</h3>
                    <Badge className={`text-[10px] w-fit ${priorityColors[phase.priority]}`}>{phase.theme}</Badge>
                  </div>
                  <div className="space-y-2 mb-4">
                    {phase.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2.5">
                        {item.critical ? (
                          <AlertTriangle className="h-3.5 w-3.5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                        ) : (
                          <CheckCircle className="h-3.5 w-3.5 text-[#0F392B]/50 mt-0.5 flex-shrink-0" />
                        )}
                        <span className={`text-sm ${item.critical ? 'font-medium' : 'text-muted-foreground'}`}>{item.text}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-[#F9F9F7] rounded-lg p-3 border border-[#E2E2E2]">
                    <p className="text-xs text-muted-foreground"><Star className="h-3 w-3 text-[#D4AF37] inline mr-1" /><span className="font-medium text-foreground">Pro tip:</span> {phase.tip}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#0F392B]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Calendar className="h-8 w-8 text-[#D4AF37] mx-auto mb-4" />
          <h3 className="text-2xl font-medium text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Never miss a deadline again</h3>
          <p className="text-sm text-white/60 mb-6">The portal tracks every deadline, task, and milestone automatically. Plus a 45-minute session with a former admissions officer to map your personal timeline.</p>
          <Link to="/sign-up"><Button className="bg-white text-[#0F392B] hover:bg-white/90 h-12 px-8 text-base" data-testid="timeline-guide-cta">Access the Portal — $99 <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
